import { useState, useCallback, useEffect } from 'react'
import { useRetailerId, usePartnerId } from 'utils/routing/params.hooks'
import { useCatalogSearchQueryParamsBehaviour } from 'legacy/sections/catalog/components/search/utils/catalogSearchQueryParams.hooks'
import {
  searchProducts,
  type SearchApiFetchParams,
} from 'legacy/sections/catalog/components/search/utils/catalogSearch.api'
import {
  type SearchApiFetchProductsResponse,
  type SearchProduct,
} from 'legacy/sections/catalog/components/search/utils/catalogSearch.types'
import {
  type CatalogSearchType,
  SEARCH_TYPES,
} from 'legacy/sections/catalog/components/search/utils/catalogSearch.constants'
import { trackEvent } from 'utils/events/trackEvent'
import { useDepartments } from 'legacy/common/utils/catalog/departments/departments.hooks'
import { useAisles } from 'legacy/common/utils/catalog/aisles/aisles.hooks'
import {
  dataFetchingLoading,
  type DataFetchingState,
  dataFetchingSuccess,
  dataFetchingError,
} from 'legacy/common/utils/data-fetching/dataFetching.types'
import { limitOffsetToPage } from 'legacy/common/utils/pagination/pagination.utils'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { normalizeProductCodes } from 'utils/product-codes/normalizeProductCodes'
import {
  type InstacartCustomersSharedV1Locale,
  type RetailerProductSearchQueryVariables,
  useRetailerProductAggregateDataLazyQuery,
  useRetailerProductSearchLazyQuery,
} from '__codegen__/api'

interface FilterParams {
  departmentId?: number
  aisleId?: number
  available?: boolean
  hasImage?: boolean
}

interface SearchParams {
  searchTerm?: string
  searchType?: CatalogSearchType
  locale?: string
}

interface SearchQueryParams extends FilterParams, SearchParams {}

interface FetchSearchedProductsProps {
  limit: number
  offset: number
  filters: Partial<SearchQueryParams>
}

const initialApiState = dataFetchingSuccess<SearchApiFetchProductsResponse>({ data: [], meta: {} })

// NOTE that this is *only* used for searching products in the Aisles > Add Products modal.
// Actual product search has moved to its own domain (products)
// This can be removed completely once we fully deprecate/remove Depts/Aisle
const useSearchResults = (useMesh: boolean) => {
  const partnerId = usePartnerId()
  const retailerId = useRetailerId()
  const { warehouse } = useWarehouseContext()
  const { queryParams } = useCatalogSearchQueryParamsBehaviour()
  const localeCode = queryParams.locale || warehouse.defaultLocale.localeCode
  const hasMultipleLocales = warehouse.locales.length > 1

  const [apiState, setApiState] =
    useState<DataFetchingState<SearchApiFetchProductsResponse>>(initialApiState)

  const [executeSearchQuery, { loading: searchLoading, error: searchError, data: searchData }] =
    useRetailerProductSearchLazyQuery()
  const [
    executeAdditionalInfoQuery,
    { loading: additionalInfoLoading, error: additionalInfoError, data: additionalInfoData },
  ] = useRetailerProductAggregateDataLazyQuery()

  const callSearchProductsMesh = useCallback(
    async (props: SearchApiFetchParams) => {
      const { searchTerm, searchType, aisleId, departmentId, pageNumber } = props
      const limit = 10
      const offsetValue = limit * (pageNumber - 1)

      const variables: RetailerProductSearchQueryVariables = {
        retailerId: retailerId,
        limit: limit.toString(),
        offset: offsetValue.toString(),
        aisleIds: aisleId ? [aisleId.toString()] : undefined,
        departmentIds: departmentId ? [departmentId.toString()] : undefined,
        localeCode: localeCode.toUpperCase() as InstacartCustomersSharedV1Locale,
      }

      if (searchType === SEARCH_TYPES.NAME) {
        variables.query = searchTerm
      } else if (searchType === SEARCH_TYPES.RRC) {
        variables.retailerReferenceCodes = searchTerm.split(/[\s,]+/).map(term => term.trim())
      } else if (searchType === SEARCH_TYPES.UPC) {
        variables.productCodes = normalizeProductCodes(
          searchTerm.split(/[\s,]+/).map(term => term.trim())
        )
      }

      executeSearchQuery({
        variables,
      })
    },
    [executeSearchQuery, retailerId, localeCode]
  )

  // Handle when we get new search data
  useEffect(() => {
    if (searchError) {
      setApiState(dataFetchingError(searchError))
      return
    }

    if (searchLoading || !searchData?.retailerToolsServiceGetRetailerProducts) return

    const results = searchData?.retailerToolsServiceGetRetailerProducts?.results
    if (!results || results.length === 0) {
      setApiState(dataFetchingSuccess({ data: [], meta: { total_count: 0 } }))
      return
    }

    const totalCount = searchData?.retailerToolsServiceGetRetailerProducts?.totalCount
    const data: SearchProduct[] = results.map(r => {
      return {
        id: parseInt(r.productId),
        image_url: r.primaryImageUrl,
        display_name: r.productDisplayName,
        product_codes: normalizeProductCodes(r.productCodes || []),
        price_max: null, // comes from additionInfoQuery
        price_min: null, // comes from additionInfoQuery
        available_items_count: null, // comes from additionInfoQuery
        global_scan_codes: [],
        has_image: !!r.primaryImageUrl,
        main_aisle: {
          id: parseInt(r.primaryAisleId, 10),
          display_name: r.aisles?.find(aisle => aisle.id == r.primaryAisleId)?.name,
          department_id: parseInt(r.primaryDepartmentId, 10),
        },
        retailer_reference_codes: r.retailerReferenceCode
          ? [
              {
                reference_code: r.retailerReferenceCode,
                warehouse_id: null,
              },
            ]
          : [],
        aisles: (r.aisles || []).map(({ id, name }) => ({ id: parseInt(id, 10), name })),
      }
    })

    // Kick off loading of additional data
    const productIds = data.map(product => product.id.toString())
    executeAdditionalInfoQuery({
      variables: {
        retailerId,
        productIds,
      },
    })

    const searchResults = {
      data,
      meta: {
        total_count: parseInt(totalCount, 10),
      },
    }
    setApiState(dataFetchingSuccess(searchResults))
  }, [searchData, searchError, searchLoading, executeAdditionalInfoQuery, retailerId, setApiState])

  // Handle merging of additional data once loaded
  useEffect(() => {
    // Don't render error if additional data fails (as search is still usable). TODO: capture sentry exception
    if (additionalInfoLoading || additionalInfoError || !additionalInfoData) return

    const currentSearchResults = apiState.data
    const retailerProductApiResult = additionalInfoData.catalogToolsApiServiceGetRetailerProductData

    const merged = []

    for (let i = 0; i < currentSearchResults.data.length; i++) {
      merged.push({
        ...currentSearchResults.data[i],
        ...retailerProductApiResult.retailerProductData.find(
          product => product.productId === currentSearchResults.data[i].id.toString()
        ),
      })
    }
    currentSearchResults.data = merged
    const newSearchResults = {
      ...currentSearchResults,
      data: merged,
    }

    setApiState(dataFetchingSuccess(newSearchResults))
  }, [additionalInfoLoading, additionalInfoError, additionalInfoData, setApiState]) // TODO: ignore warning. Including apiState in deps causes infinite loop

  const callSearchProductsLegacy = useCallback(
    (props: SearchApiFetchParams) => {
      searchProducts(props)
        .then(result => {
          setApiState(dataFetchingSuccess(result))
        })
        .catch((err?: Error) => {
          setApiState(dataFetchingError(err))
        })
    },
    [setApiState]
  )
  const callSearchProducts = useMesh ? callSearchProductsMesh : callSearchProductsLegacy

  const fetchSearchedProducts = useCallback(
    ({ limit, offset, filters }: FetchSearchedProductsProps) => {
      const pageNumber = limitOffsetToPage({ offset, limit })

      const searchApiProps: SearchApiFetchParams = {
        partnerId,
        retailerId,
        pageNumber,
        ...filters,
      }

      if (!filters.searchTerm || !filters.searchType) {
        return
      }

      setApiState(previousState => dataFetchingLoading({ previousState }))

      callSearchProducts(searchApiProps)
    },
    [callSearchProducts, partnerId, retailerId]
  )

  const clearSearchResults = () => {
    setApiState(initialApiState)
  }

  return {
    searchResults: apiState.data || { data: [], meta: {} },
    clearSearchResults,
    searchResultsLoading: apiState.loading,
    searchResultsError: apiState.error,
    fetchSearchedProducts,
    localeCode,
    hasMultipleLocales,
  }
}

export const useCatalogSearchBehaviour = (useMesh = false) => {
  const { queryParams, setQueryParams } = useCatalogSearchQueryParamsBehaviour()

  const {
    searchResults,
    clearSearchResults,
    searchResultsLoading,
    searchResultsError,
    fetchSearchedProducts,
    localeCode,
    hasMultipleLocales,
  } = useSearchResults(useMesh)

  const { departments, departmentsLoading } = useDepartments()

  const { aisles, clearAisles, aislesLoading } = useAisles(queryParams.departmentId)

  const updateSearchParams = (searchParams: SearchParams) => {
    trackEvent({
      id: 'catalog.search.search_results.clicked_search',
      description: 'Clicked the apply search button from the search results page',
      data: {
        searchType: queryParams.searchType,
        searchTerm: queryParams.searchTerm,
        departmentId: queryParams.departmentId,
        aisleId: queryParams.aisleId,
        available: queryParams.available,
        locale: queryParams.locale,
      },
    })

    setQueryParams({
      ...searchParams,
    })
  }

  const updateFilter = (filterParams: FilterParams) => {
    setQueryParams({ ...filterParams })
  }

  const clearSearch = () => {
    trackEvent({
      id: 'catalog.search.search_results.clicked_clear',
      description: 'Clicked the clear button from the search results page',
    })

    clearSearchResults()
    clearAisles()
    setQueryParams({}, 'replace')
  }

  return {
    queryParams,
    setQueryParams,
    updateSearchParams,
    updateFilter,
    fetchSearchedProducts,
    departments,
    departmentsLoading,
    searchResults,
    searchResultsLoading,
    searchResultsError,
    aisles,
    aislesLoading,
    clearSearch,
    clearSearchResults,
    localeCode,
    hasMultipleLocales,
  }
}

export type CatalogSearchBehaviour = ReturnType<typeof useCatalogSearchBehaviour>
